const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Container, Col, Row, Table } = require('reactstrap')
const { useBlockchainData, useFormatBytes, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, Tabs } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')

require('@styles/pages/blocks/index.css')

const Validators = () => {
    const location = useLocation()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const { getValidators } = useBlockchainData(network.name)
    const formatBytes = useFormatBytes()

    const [activeTab, setActiveTab] = useState(() => {
        const urlHash = window.location.hash.slice(1)
        return urlHash || 'overview'
    })

    const params = new URLSearchParams(location.search)

    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)
    const [validatorsData, setValidatorsData] = useState()

    const isMobile = useResponsive(BREAKPOINTS.medium)

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)

            try {
                const [validatorsResponse] = await Promise.all([getValidators(page, limit, activeTab)])

                if (validatorsResponse) {
                    setValidatorsData(validatorsResponse)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [activeTab, network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Validators</text>
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <Tabs
                        activeTab={activeTab}
                        items={[
                            { tab: 'overview', text: 'Overview' },
                            { tab: 'active', text: 'Active' },
                            { tab: 'pending', text: 'Pending' },
                            { tab: 'exited', text: 'Exited' }
                        ]}
                        onTabChange={setActiveTab}
                    />
                    <div className='box table-section' style={{ padding: '24px 24px 0' }}>
                        {loading ? (
                            <div
                                className='d-flex justify-content-center align-items-center'
                                style={{ height: '300px' }}
                            >
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <TableHeader
                                    title={`Total of ${validatorsData?.totalItems.toLocaleString()} validators`}
                                    page={page}
                                    limit={limit}
                                    totalPages={validatorsData?.totalPages}
                                />
                                <Table
                                    responsive
                                    style={
                                        isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}
                                    }
                                >
                                    <thead>
                                        <tr>
                                            <th>Index</th>
                                            <th>Public Key</th>
                                            <th>Current Balance</th>
                                            <th>Eff. Balance</th>
                                            <th>Eligibility</th>
                                            <th>Activation</th>
                                            <th>Exit</th>
                                            <th>Withdrawable</th>
                                            <th>Slashed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {validatorsData?.items.map(validator => (
                                            <tr key={validator.index}>
                                                <td>
                                                    <i
                                                        className='fas fa-user'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)',
                                                            marginRight: '10px'
                                                        }}
                                                    />
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/validator/${validator.index}`
                                                            )
                                                        }
                                                    >
                                                        {validator.index}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/validator/${validator.index}`
                                                            )
                                                        }
                                                    >
                                                        {formatBytes(validator.pubkey, 64, 10, 10)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {(validator.balance / 10 ** 9).toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 9
                                                    })}
                                                    &nbsp;{network.data.token}
                                                </td>
                                                <td>
                                                    {(validator.effectiveBalance / 10 ** 9).toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 9
                                                    })}
                                                    &nbsp;{network.data.token}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/epoch/${validator.activationEligibilityEpoch}`
                                                            )
                                                        }
                                                    >
                                                        {validator.activationEligibilityEpoch === 0
                                                            ? 'genesis'
                                                            : validator.activationEligibilityEpoch}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/epoch/${validator.activationEpoch}`
                                                            )
                                                        }
                                                    >
                                                        {validator.activationEpoch === 0
                                                            ? 'genesis'
                                                            : validator.activationEpoch}
                                                    </span>
                                                </td>
                                                <td>
                                                    {validator.status === 'active_ongoing' ? (
                                                        '-'
                                                    ) : (
                                                        <span
                                                            className={`${network.name}-text-color cursor-pointer`}
                                                            onClick={() =>
                                                                history.push(
                                                                    `/${network.name}/epoch/${validator.activationEpoch}`
                                                                )
                                                            }
                                                        >
                                                            {validator.exitEpoch}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/epoch/${validator.withdrawableEpoch}`
                                                            )
                                                        }
                                                    >
                                                        {validator.withdrawableEpoch}
                                                    </span>
                                                </td>
                                                <td>{validator.slashed.toString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <TableFooter
                                    limit={limit}
                                    setLimit={setLimit}
                                    page={page}
                                    setPage={setPage}
                                    totalPages={validatorsData?.totalPages}
                                />
                            </>
                        )}
                    </div>
                </Col>
            </Container>
        </div>
    )
}

module.exports = Validators
